import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { api, ApiError } from "./api";
import type { Account } from "./types";

interface AuthState {
  account: Account | null;
  loading: boolean;
  refresh: () => Promise<void>;
  login: (email: string, password: string, keepSignedIn: boolean) => Promise<void>;
  signup: (name: string, email: string, password: string, drinkingAgeConfirmed: boolean) => Promise<void>;
  logout: () => Promise<void>;
  setAccount: (account: Account) => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { account } = await api.get<{ account: Account }>("/me");
      setAccount(account);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) setAccount(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email: string, password: string, keepSignedIn: boolean) => {
    const { account } = await api.post<{ account: Account }>("/auth/login", { email, password, keepSignedIn });
    setAccount(account);
  }, []);

  const signup = useCallback(
    async (name: string, email: string, password: string, drinkingAgeConfirmed: boolean) => {
      const { account } = await api.post<{ account: Account }>("/auth/signup", {
        name,
        email,
        password,
        drinkingAgeConfirmed,
      });
      setAccount(account);
    },
    []
  );

  const logout = useCallback(async () => {
    await api.post("/auth/logout");
    setAccount(null);
  }, []);

  return (
    <AuthContext.Provider value={{ account, loading, refresh, login, signup, logout, setAccount }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
